import type { TFunction } from 'i18next';
import { newCOLORS } from '../../styles/colors';

type ChartType = 'line' | 'area' | 'bar' | 'stacked_bar' | 'pie' | 'text' | 'burn_up' | 'burn_down' | 'cumulative_flow';

const DATE_RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'last_1_day', label: 'Last 1 Day' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_14_days', label: 'Last 14 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_60_days', label: 'Last 60 Days' },
  { value: 'last_90_days', label: 'Last 90 Days' },
  { value: 'custom', label: 'Custom' },
];

/**
 * Build the sprint-based date range options. Module-level constants
 * initialized with i18n.t() would freeze the labels at import — accept the
 * caller's translation function so org-level overrides apply.
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns Array of `{ value, label }` options for sprint date-range pickers
 */
const getSprintDateRanges = (t: TFunction) => [
  { value: 'last_sprint', label: `Last ${t('common.sprint')}` },
  { value: 'last_2_sprints', label: `Last 2 ${t('common.sprints')}` },
  { value: 'last_3_sprints', label: `Last 3 ${t('common.sprints')}` },
  { value: 'last_4_sprints', label: `Last 4 ${t('common.sprints')}` },
  { value: 'last_5_sprints', label: `Last 5 ${t('common.sprints')}` },
  { value: 'last_6_sprints', label: `Last 6 ${t('common.sprints')}` },
];

/**
 * Build the dashboard widget's object-type options against the caller's
 * translation function so org-level overrides apply.
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns Array of `{ value, label }` options for the object-type select
 */
const getObjectTypes = (t: TFunction) => [
  { value: 'board', label: t('common.board') },
  { value: 'project', label: t('common.project') },
  { value: 'portfolio', label: t('common.portfolio') },
  { value: 'initiative', label: t('common.initiative') },
  { value: 'sprint', label: t('common.sprint') },
];

const WIDGET_TYPES = [
  { value: 'table', label: 'Table' },
  { value: 'tile', label: 'Tile' },
  { value: 'line', label: 'Line Chart' },
  { value: 'area', label: 'Area Chart' },
  { value: 'bar', label: 'Bar Chart' },
  { value: 'stacked_bar', label: 'Stacked Bar Chart' },
  { value: 'pie', label: 'Pie Chart' },
  { value: 'burn_up', label: 'Burn Up Chart' },
  { value: 'burn_down', label: 'Burn Down Chart' },
  { value: 'cumulative_flow', label: 'Cumulative Flow Chart' },
];

const TEXT_WIDGET_TYPES = [{ value: 'text', label: 'Text' }];

const WIDGET_SIZES = [{ value: 'standard', label: 'Standard' }];

const COLORS = {
  primary: {
    stroke: newCOLORS.darkPurple,
    fill: `${newCOLORS.darkPurple}20`,
  },
  success: {
    stroke: newCOLORS.green,
    fill: `${newCOLORS.green}20`,
  },
  danger: {
    stroke: newCOLORS.red,
    fill: `${newCOLORS.red}20`,
  },
  warning: {
    stroke: newCOLORS.orange,
    fill: `${newCOLORS.orange}20`,
  },
  info: {
    stroke: newCOLORS.blue,
    fill: `${newCOLORS.blue}20`,
  },
  purple: {
    stroke: newCOLORS.purple,
    fill: `${newCOLORS.purple}20`,
  },
  pink: {
    stroke: newCOLORS.pink,
    fill: `${newCOLORS.pink}20`,
  },
  indigo: {
    stroke: newCOLORS.indigo,
    fill: `${newCOLORS.indigo}20`,
  },
  teal: {
    stroke: newCOLORS.teal,
    fill: `${newCOLORS.teal}20`,
  },
  yellow: {
    stroke: newCOLORS.yellow,
    fill: `${newCOLORS.yellow}20`,
  },
};

export { COLORS, DATE_RANGES, getObjectTypes, getSprintDateRanges, TEXT_WIDGET_TYPES, WIDGET_SIZES, WIDGET_TYPES };

export type { ChartType };
